/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { EGeoDataEntity, IGeoData } from 'types/geodata.types';
import { DialogDefault, ThemeButton, ThemeTypography } from 'designSystem';
import { Box, DialogContent, styled } from '@mui/material';
import { FlexBox, PageSubTitle } from 'components/Structure';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { useUserData } from 'components/hooks';
import { booleanish } from 'types/booleanish.types';
import round from 'lodash/round';

interface IPricingInformationDialogProps extends IDefaultDialogProps {
  geoDatas: IGeoData[];
  onSubmitAnalysis: () => void;
}

const InfoContainer = styled(FlexBox)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  background: theme.custom.themeColors.primary[5],
}));

const StyledList = styled('ul')(({ theme }) => ({
  padding: theme.spacing(0, 0, 0, 2),
  margin: 0,
}));

const Table = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  borderRadius: '6px',
  width: 'fit-content',
  background: 'white',
}));

const ContingentStatus = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.accent[20],
  borderRadius: '6px',
  padding: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const TableCell = styled(Box)<{
  isFirstRow?: booleanish;
  isLastColumn?: booleanish;
}>(({ theme, isFirstRow, isLastColumn }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  borderTop: isFirstRow !== 'true' ? `1px solid ${theme.custom.themeColors.primary[40]}` : 'none',
  borderRight:
    isLastColumn !== 'true' ? `1px solid ${theme.custom.themeColors.primary[40]}` : 'none',
  padding: theme.spacing(1),
  width: 218,
}));

const PricingInformationDialog: FC<IPricingInformationDialogProps> = ({
  open,
  geoDatas,
  onClose,
  onSubmitAnalysis,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { company } = useUserData();

  const totalPlotsArea = useMemo(() => {
    return geoDatas.reduce((acc, geoData) => acc + (geoData.totalPlotsArea || 0), 0);
  }, [geoDatas]);

  const handleSubmitAnalysis = async () => {
    try {
      setIsLoading(true);
      await onSubmitAnalysis();
      onClose?.();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const yearlyMaximumAnalysisAreaAllowance =
    company?.planDetails?.yearlyMaximumAnalysisAreaAllowance || 0;
  const yearlyAnalysisAreaUsage = company?.planDetails?.yearlyAnalysisAreaUsage || 0;
  const pricePerHectare = company?.planDetails?.pricePerHectare || 0;

  const estimatedCost = useMemo(() => {
    if (yearlyAnalysisAreaUsage >= yearlyMaximumAnalysisAreaAllowance) {
      return pricePerHectare * totalPlotsArea;
    }

    if (yearlyAnalysisAreaUsage + totalPlotsArea > yearlyMaximumAnalysisAreaAllowance) {
      return (
        pricePerHectare *
        (totalPlotsArea - (yearlyMaximumAnalysisAreaAllowance - yearlyAnalysisAreaUsage))
      );
    }

    return 0;
  }, [
    pricePerHectare,
    totalPlotsArea,
    yearlyAnalysisAreaUsage,
    yearlyMaximumAnalysisAreaAllowance,
  ]);

  return (
    <DialogDefault
      open={open}
      title="Start deforestation risk analysis"
      data-cy="pricing-information-dialog"
      fullWidth
      maxWidth="md"
      onClose={onClose}
    >
      <DialogContent>
        <InfoContainer>
          <ThemeTypography variant="BODY_LARGER_BOLD" color="ALMOST_BLACK">
            Are you sure you want to start a new deforestation risk analysis?
          </ThemeTypography>
          <Box mt={1} />
          <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
            Starting the analysis may result in costs for your company account. The cost of the
            analysis depends on:
            <StyledList>
              <li>The total area analysed (giving the cost per hectare). </li>
              <li>
                If the same identical plots have already been analysed within the past 12 months
                with a valid check date (the results can be re-used without extra cost).
              </li>
              <li>
                Single-point plots without a given size is assumed to be 4 ha in the deforestation
                analysis, according to regulation requirements
              </li>
            </StyledList>
          </ThemeTypography>
        </InfoContainer>
        <InfoContainer mt={2} pt={1}>
          <ThemeTypography variant="BODY_LARGE" color="ALMOST_BLACK">
            Estimated cost:{' '}
            <b>{round(estimatedCost, 2).toLocaleString('en-GB').replace(/,/g, ' ')} EUR</b>
          </ThemeTypography>
          <Box mt={1.5} />
          <Table>
            <Box display="flex">
              <TableCell isFirstRow="true">
                <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
                  Total area to be analyzed
                </ThemeTypography>
              </TableCell>
              <TableCell isFirstRow="true" isLastColumn="true">
                <ThemeTypography variant="BODY_MEDIUM_BOLD" color="ALMOST_BLACK">
                  {round(totalPlotsArea, 2).toLocaleString('en-GB').replace(/,/g, ' ')} ha
                </ThemeTypography>
              </TableCell>
            </Box>
            <Box display="flex">
              <TableCell>
                <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
                  Contract-specific agreements
                </ThemeTypography>
              </TableCell>
              <TableCell isLastColumn="true">
                <ThemeTypography variant="BODY_MEDIUM_BOLD" color="ALMOST_BLACK">
                  {yearlyMaximumAnalysisAreaAllowance.toLocaleString('en-GB').replace(/,/g, ' ')} ha
                  per year free of charge
                </ThemeTypography>
              </TableCell>
            </Box>
          </Table>
          <Box mt={1.5} />
          <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
            This cost will be billed on your account for the current calendar month.
          </ThemeTypography>
        </InfoContainer>
        <ContingentStatus>
          Annual account contingent status (analysis free of charge):{' '}
          <b>
            {round(yearlyAnalysisAreaUsage, 0).toLocaleString('en-GB').replace(/,/g, ' ')} /{' '}
            {round(yearlyMaximumAnalysisAreaAllowance, 0)
              .toLocaleString('en-GB')
              .replace(/,/g, ' ')}{' '}
            ha
          </b>
        </ContingentStatus>
        <Box mt={2} mb={3}>
          <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
            Once started, the analysis can take up to 24 hours. We will notify you in the
            application and by email when the analysis is completed.
          </ThemeTypography>
        </Box>

        <FlexBox mt={3} mb={1} justifyContent="space-between">
          <ThemeButton color="BLUE_ICE" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton
            startIcon={<Icon name="analysis" />}
            onClick={handleSubmitAnalysis}
            loading={isLoading}
          >
            Start risk analysis
          </ThemeButton>
        </FlexBox>
      </DialogContent>
    </DialogDefault>
  );
};

export default PricingInformationDialog;
